import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { ThumbUp as ThumbUpIcon, ThumbUpOutlined as ThumbUpOutlinedIcon } from '@mui/icons-material';
import apiService from '../services/api';

const QuizModal = ({ isOpen, onClose, subject, chapter, quiz, questions, contentId = null }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [updatingLike, setUpdatingLike] = useState(false);
  const [questionLikes, setQuestionLikes] = useState({});
  const [updatingQuestionLike, setUpdatingQuestionLike] = useState({});

  const quizQuestions = questions || quiz?.questions || [];

  useEffect(() => {
    if (isOpen && quizQuestions.length > 0) {
      setTimeLeft(quizQuestions.length * 60); // 1 minute per question
      setCurrentQuestion(0);
      setSelectedAnswers({});
      setIsCompleted(false);
      setScore(0);
    }
  }, [isOpen, quizQuestions]);

  useEffect(() => {
    if (timeLeft > 0 && !isCompleted) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft, isCompleted]);

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    let correctAnswers = 0;
    quizQuestions.forEach((question, index) => {
      if (selectedAnswers[index] === question.correct_answer) {
        correctAnswers++;
      }
    });
    setScore(correctAnswers);
    setIsCompleted(true);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLike = async () => {
    if (!contentId || updatingLike) return;
    
    setUpdatingLike(true);
    try {
      await apiService.likeContent(contentId, 'quiz', !isLiked);
      setIsLiked(!isLiked);
    } catch (error) {
      console.error('Error updating like status:', error);
    } finally {
      setUpdatingLike(false);
    }
  };

  const handleQuestionLike = async (questionIndex, liked) => {
    if (updatingQuestionLike[questionIndex]) return;
    
    setUpdatingQuestionLike(prev => ({ ...prev, [questionIndex]: true }));
    try {
      // For now, we'll save the like status locally since we don't have individual question IDs
      setQuestionLikes(prev => ({ ...prev, [questionIndex]: liked }));
      // TODO: Add API endpoint for individual question likes
    } catch (error) {
      console.error('Error updating question like status:', error);
    } finally {
      setUpdatingQuestionLike(prev => ({ ...prev, [questionIndex]: false }));
    }
  };

  const currentQ = quizQuestions[currentQuestion];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <AssignmentIcon className="text-purple-500" />
                <div>
                  <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">
                    {chapter} - Quiz
                  </h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Question {currentQuestion + 1} of {quizQuestions.length}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {!isCompleted && (
                  <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                    <TimerIcon className="w-4 h-4" />
                    <span className="font-mono font-bold text-sm">{formatTime(timeLeft)}</span>
                  </div>
                )}
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-100px)]">
              {/* Information Message */}
              {!isCompleted && Object.keys(selectedAnswers).length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    💡 <strong>Tip:</strong> You can leave this quiz anytime and return to it later from your quiz list. Your progress will be saved.
                  </p>
                </div>
              )}

              {isCompleted ? (
                <div className="text-center py-6">
                  <CheckCircleIcon className="text-green-500 text-4xl mx-auto mb-2" />
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-1">
                    Quiz Completed!
                  </h3>
                  <p className="text-base text-gray-600 dark:text-gray-400 mb-2">
                    You scored {score} out of {quizQuestions.length}
                  </p>
                  <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                    {Math.round((score / quizQuestions.length) * 100)}%
                  </div>
                </div>
              ) : currentQ ? (
                <div className="space-y-4">
                  {/* Compact Question Display */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-3 border border-purple-200 dark:border-purple-600">
                    <span className="inline-block px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold rounded-full mb-2">
                      Q{currentQuestion + 1} of {quizQuestions.length}
                    </span>
                    <h3 className="text-base font-bold text-gray-800 dark:text-gray-200 leading-snug mb-2">
                      {currentQ.question}
                    </h3>
                  </div>
                  {/* Compact Answer Options */}
                  <div className="space-y-1">
                    {currentQ.options?.map((option, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => handleAnswerSelect(currentQuestion, index)}
                        className={`w-full p-3 text-left rounded-md border transition-all duration-200 text-sm font-medium ${
                          selectedAnswers[currentQuestion] === index
                            ? 'border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 shadow'
                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-500 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            selectedAnswers[currentQuestion] === index 
                              ? 'border-purple-500 bg-purple-500' 
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {selectedAnswers[currentQuestion] === index && (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                          <span className="text-gray-800 dark:text-gray-200 font-medium text-sm">
                            {option}
                          </span>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  No questions available
                </div>
              )}
            </div>

            {/* Footer Navigation */}
            {!isCompleted && currentQ && (
              <div className="flex items-center justify-between p-3 border-t border-gray-200 dark:border-gray-700">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-md hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-xs font-medium"
                >
                  ← Prev
                </motion.button>
                <div className="flex gap-1">
                  {Array.from({ length: quizQuestions.length }, (_, i) => (
                    <motion.button
                      key={i}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setCurrentQuestion(i)}
                      className={`w-7 h-7 rounded-full text-xs font-bold transition-all duration-200 ${
                        i === currentQuestion
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow'
                          : selectedAnswers[i] !== undefined
                          ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-sm'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {i + 1}
                    </motion.button>
                  ))}
                </div>
                {currentQuestion === quizQuestions.length - 1 ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSubmit}
                    className="px-4 py-1 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-md hover:shadow-md transition-all duration-200 text-xs font-medium"
                  >
                    Submit
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleNext}
                    className="px-3 py-1 bg-gradient-to-r from-teal-500 to-green-600 text-white rounded-md hover:shadow-md transition-all duration-200 text-xs font-medium"
                  >
                    Next →
                  </motion.button>
                )}
              </div>
            )}

            {/* Like Box for Entire Quiz Set */}
            {currentQuestion === quizQuestions.length - 1 && !isCompleted && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex justify-center p-4"
              >
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 text-center">
                    How helpful was this quiz?
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleLike()}
                      disabled={updatingLike}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                        isLiked 
                          ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-2 border-green-300 dark:border-green-600'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                      }`}
                    >
                      {updatingLike ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : isLiked ? (
                        <ThumbUpIcon className="w-4 h-4" />
                      ) : (
                        <ThumbUpOutlinedIcon className="w-4 h-4" />
                      )}
                      <span className="font-medium">Helpful</span>
                    </button>
                    
                    <button
                      onClick={() => handleLike()}
                      disabled={updatingLike}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                        !isLiked && isLiked !== null
                          ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-2 border-red-300 dark:border-red-600'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/30'
                      }`}
                    >
                      {updatingLike ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : !isLiked && isLiked !== null ? (
                        <ThumbUpOutlinedIcon className="w-4 h-4 transform rotate-180" />
                      ) : (
                        <ThumbUpOutlinedIcon className="w-4 h-4 transform rotate-180" />
                      )}
                      <span className="font-medium">Not Helpful</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuizModal; 